import { useEffect, useRef, useState } from 'react';
import {
  applyCanvasItemConfig,
  buildCanvasFileName,
  cloneDefaultCanvas,
  copyCanvasItem,
  createCanvasItem,
  distributeProjectionItems,
  getCanvasClipboardAction,
  getCanvasItemBounds,
  normalizeCanvasItemConfig,
  validateCanvasDocument,
} from './canvas';
import { CanvasControls } from './components/CanvasControls';
import { CanvasExportDialog, type CanvasExportSelection } from './components/CanvasExportDialog';
import { CanvasPreview } from './components/CanvasPreview';
import { Controls } from './components/Controls';
import { Preview } from './components/Preview';
import { RatioGuard } from './components/RatioGuard';
import { Tutorial } from './components/Tutorial';
import { buildFileName, cloneDefaultConfig, validateConfig } from './config';
import {
  downloadCanvasJson,
  downloadJson,
  exportCanvasArchive,
  exportCanvasPng,
  exportPng,
} from './exporter';
import { useUndoableState } from './history';
import {
  deleteCanvasProject,
  deleteProject,
  listCanvasProjects,
  listProjects,
  loadCanvasDraft,
  loadDraft,
  saveCanvasDraft,
  saveCanvasProject,
  saveDraft,
  saveProject,
} from './storage';
import type {
  CanvasDocument,
  CanvasItem,
  CanvasType,
  GeneratorConfig,
  StoredCanvasProject,
  StoredProject,
} from './types';
import { validateExportFileName, validatePngFile } from './validation';

interface Notice {
  kind: 'success' | 'error' | 'info';
  message: string;
}

type WorkspaceMode = 'single' | 'canvas' | 'ratio' | 'tutorial';

export default function App() {
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>(getInitialWorkspace);
  const {
    value: config,
    set: setConfig,
    reset: resetConfig,
    undo: undoConfig,
    redo: redoConfig,
    beginBatch: beginConfigBatch,
    endBatch: endConfigBatch,
    canUndo: canUndoConfig,
    canRedo: canRedoConfig,
  } = useUndoableState<GeneratorConfig>(cloneDefaultConfig());
  const {
    value: canvas,
    set: setCanvas,
    reset: resetCanvas,
    undo: undoCanvas,
    redo: redoCanvas,
    beginBatch: beginCanvasBatch,
    endBatch: endCanvasBatch,
    canUndo: canUndoCanvas,
    canRedo: canRedoCanvas,
  } = useUndoableState<CanvasDocument>(cloneDefaultCanvas());
  const [projects, setProjects] = useState<StoredProject[]>([]);
  const [canvasProjects, setCanvasProjects] = useState<StoredCanvasProject[]>([]);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [currentCanvasProjectId, setCurrentCanvasProjectId] = useState<string | null>(null);
  const [selectedCanvasScreenIds, setSelectedCanvasScreenIds] = useState<string[]>([]);
  const [editingCanvasScreenId, setEditingCanvasScreenId] = useState<string | null>(null);
  const [canvasClipboard, setCanvasClipboard] = useState<CanvasItem | null>(null);
  const [ready, setReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [canvasExportDialogOpen, setCanvasExportDialogOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const initialFileName = buildFileName(config);
  const initialCanvasFileName = buildCanvasFileName(canvas);
  const [fileName, setFileName] = useState(initialFileName);
  const [canvasFileName, setCanvasFileName] = useState(initialCanvasFileName);
  const previousDefaultFileName = useRef(initialFileName);
  const previousDefaultCanvasFileName = useRef(initialCanvasFileName);
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const canvasPasteOffsetRef = useRef(32);
  const activeFileName = workspaceMode === 'canvas' ? canvasFileName : fileName;
  const fileNameError = validateExportFileName(activeFileName);
  const editingScreen = canvas.items.find((item) => item.id === editingCanvasScreenId) ?? null;
  const selectedCanvasScreenId = selectedCanvasScreenIds.at(-1) ?? null;
  const activeGeneratorConfig = editingScreen?.config ?? config;

  useEffect(() => {
    void Promise.all([loadDraft(), loadCanvasDraft(), listProjects(), listCanvasProjects()])
      .then(([draft, canvasDraft, storedProjects, storedCanvases]) => {
        if (draft) resetConfig(validateConfig(draft));
        if (canvasDraft) resetCanvas(validateCanvasDocument(canvasDraft));
        setProjects(storedProjects);
        setCanvasProjects(storedCanvases);
      })
      .catch(() => setNotice({ kind: 'error', message: 'Browser storage is unavailable.' }))
      .finally(() => setReady(true));
  }, [resetCanvas, resetConfig]);

  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => {
      void saveDraft(config).catch(() =>
        setNotice({ kind: 'error', message: 'Could not auto-save the current screen.' }),
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [config, ready]);

  useEffect(() => {
    if (!ready) return;
    const timer = window.setTimeout(() => {
      void saveCanvasDraft(canvas).catch(() =>
        setNotice({ kind: 'error', message: 'Could not auto-save the current Canvas.' }),
      );
    }, 300);
    return () => window.clearTimeout(timer);
  }, [canvas, ready]);

  useEffect(() => {
    let nextDefault: string;
    try {
      nextDefault = buildFileName(config);
    } catch {
      return;
    }
    if (fileName === previousDefaultFileName.current) setFileName(nextDefault);
    previousDefaultFileName.current = nextDefault;
  }, [config, fileName]);

  useEffect(() => {
    const nextDefault = buildCanvasFileName(canvas);
    if (canvasFileName === previousDefaultCanvasFileName.current) setCanvasFileName(nextDefault);
    previousDefaultCanvasFileName.current = nextDefault;
  }, [canvas, canvasFileName]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    const syncWorkspaceFromHash = () => {
      const nextMode = getInitialWorkspace();
      setWorkspaceMode(nextMode);
      if (nextMode !== 'canvas') setEditingCanvasScreenId(null);
    };
    window.addEventListener('hashchange', syncWorkspaceFromHash);
    return () => window.removeEventListener('hashchange', syncWorkspaceFromHash);
  }, []);

  const refreshProjects = async () => setProjects(await listProjects());
  const refreshCanvasProjects = async () => setCanvasProjects(await listCanvasProjects());

  const handleSaveProject = async () => {
    if (workspaceMode === 'canvas') {
      try {
        const validated = validateCanvasDocument(canvas);
        const id = currentCanvasProjectId || crypto.randomUUID();
        await saveCanvasProject({
          id,
          name: validated.title.trim() || 'Untitled Canvas',
          updatedAt: new Date().toISOString(),
          canvas: structuredClone(validated),
        });
        setCurrentCanvasProjectId(id);
        await refreshCanvasProjects();
        setNotice({ kind: 'success', message: 'Canvas project saved in this browser.' });
      } catch (error) {
        setNotice({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Could not save this Canvas.',
        });
      }
      return;
    }

    const id = currentProjectId || crypto.randomUUID();
    const project: StoredProject = {
      id,
      name: config.title.trim() || 'Untitled screen',
      updatedAt: new Date().toISOString(),
      config: structuredClone(config),
    };
    try {
      await saveProject(project);
      setCurrentProjectId(id);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Screen project saved in this browser.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not save this screen project.' });
    }
  };

  const handleNewProject = () => {
    if (workspaceMode === 'canvas') {
      resetCanvas(cloneDefaultCanvas(canvas.canvasType));
      setCurrentCanvasProjectId(null);
      setSelectedCanvasScreenIds([]);
      setEditingCanvasScreenId(null);
      setNotice({ kind: 'info', message: `New unsaved ${canvas.canvasType} Canvas created.` });
      return;
    }
    resetConfig(cloneDefaultConfig());
    setCurrentProjectId(null);
    setNotice({ kind: 'info', message: 'New unsaved screen created.' });
  };

  const handleDuplicateProject = async () => {
    try {
      if (workspaceMode === 'canvas') {
        const copyName = `${canvas.title.trim() || 'Untitled Canvas'} copy`;
        const project: StoredCanvasProject = {
          id: crypto.randomUUID(),
          name: copyName,
          updatedAt: new Date().toISOString(),
          canvas: { ...structuredClone(canvas), title: copyName },
        };
        await saveCanvasProject(project);
        resetCanvas(project.canvas);
        setCurrentCanvasProjectId(project.id);
        setSelectedCanvasScreenIds([]);
        setEditingCanvasScreenId(null);
        await refreshCanvasProjects();
        setNotice({ kind: 'success', message: 'Canvas project duplicated.' });
        return;
      }
      const copyName = `${config.title.trim() || 'Untitled screen'} copy`;
      const project: StoredProject = {
        id: crypto.randomUUID(),
        name: copyName,
        updatedAt: new Date().toISOString(),
        config: { ...structuredClone(config), title: copyName },
      };
      await saveProject(project);
      resetConfig(project.config);
      setCurrentProjectId(project.id);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Screen project duplicated.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not duplicate this project.' });
    }
  };

  const handleDeleteProject = async () => {
    if (workspaceMode === 'canvas') {
      if (!currentCanvasProjectId) return;
      const project = canvasProjects.find((item) => item.id === currentCanvasProjectId);
      if (!window.confirm(`Delete “${project?.name || 'this Canvas'}” from this browser?`)) return;
      try {
        await deleteCanvasProject(currentCanvasProjectId);
        setCurrentCanvasProjectId(null);
        await refreshCanvasProjects();
        setNotice({ kind: 'success', message: 'Canvas project deleted.' });
      } catch {
        setNotice({ kind: 'error', message: 'Could not delete this Canvas project.' });
      }
      return;
    }
    if (!currentProjectId) return;
    const project = projects.find((item) => item.id === currentProjectId);
    if (!window.confirm(`Delete “${project?.name || 'this project'}” from this browser?`)) return;
    try {
      await deleteProject(currentProjectId);
      setCurrentProjectId(null);
      await refreshProjects();
      setNotice({ kind: 'success', message: 'Screen project deleted.' });
    } catch {
      setNotice({ kind: 'error', message: 'Could not delete this screen project.' });
    }
  };

  const handleProjectSelect = (id: string) => {
    if (workspaceMode === 'canvas') {
      if (!id) {
        setCurrentCanvasProjectId(null);
        return;
      }
      const selected = canvasProjects.find((project) => project.id === id);
      if (!selected) return;
      try {
        resetCanvas(validateCanvasDocument(selected.canvas));
        setCurrentCanvasProjectId(selected.id);
        setSelectedCanvasScreenIds([]);
        setEditingCanvasScreenId(null);
        setNotice({ kind: 'info', message: `Loaded “${selected.name}”.` });
      } catch {
        setNotice({ kind: 'error', message: 'This saved Canvas project is invalid.' });
      }
      return;
    }
    if (!id) {
      setCurrentProjectId(null);
      return;
    }
    const selected = projects.find((project) => project.id === id);
    if (!selected) return;
    try {
      resetConfig(validateConfig(selected.config));
      setCurrentProjectId(selected.id);
      setNotice({ kind: 'info', message: `Loaded “${selected.name}”.` });
    } catch {
      setNotice({ kind: 'error', message: 'This saved screen project is invalid.' });
    }
  };

  const updateActiveGeneratorConfig = (updater: (current: GeneratorConfig) => GeneratorConfig) => {
    if (!editingCanvasScreenId) {
      setConfig(updater);
      return;
    }
    setCanvas((current) => ({
      ...current,
      items: current.items.map((item) => {
        if (item.id !== editingCanvasScreenId) return item;
        const nextConfig = normalizeCanvasItemConfig(updater(item.config));
        return applyCanvasItemConfig(item, nextConfig);
      }),
    }));
  };

  const handleLogoUpload = async (file: File) => {
    const validationError = await validatePngFile(file);
    if (validationError) {
      setNotice({ kind: 'error', message: validationError });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateActiveGeneratorConfig((current) => ({
        ...current,
        logo: { ...current.logo, dataUrl: String(reader.result), fileName: file.name },
      }));
    };
    reader.onerror = () => setNotice({ kind: 'error', message: 'Could not read the logo file.' });
    reader.readAsDataURL(file);
  };

  const handleJsonImport = (file: File) => {
    if (file.size > 50 * 1024 * 1024) {
      setNotice({ kind: 'error', message: 'The JSON configuration must be 50 MB or smaller.' });
      return;
    }
    if (
      file.type &&
      file.type !== 'application/json' &&
      !file.name.toLowerCase().endsWith('.json')
    ) {
      setNotice({ kind: 'error', message: 'Select a JSON configuration file.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as { documentType?: unknown };
        if (parsed.documentType === 'canvas') {
          resetCanvas(validateCanvasDocument(parsed));
          setWorkspaceMode('canvas');
          setCurrentCanvasProjectId(null);
          setSelectedCanvasScreenIds([]);
          setEditingCanvasScreenId(null);
          setNotice({ kind: 'success', message: 'Canvas JSON imported.' });
        } else {
          resetConfig(validateConfig(parsed));
          setWorkspaceMode('single');
          setCurrentProjectId(null);
          setEditingCanvasScreenId(null);
          setNotice({ kind: 'success', message: 'Screen JSON imported.' });
        }
      } catch (error) {
        setNotice({
          kind: 'error',
          message: error instanceof Error ? error.message : 'Invalid JSON configuration.',
        });
      }
    };
    reader.onerror = () => setNotice({ kind: 'error', message: 'Could not read the JSON file.' });
    reader.readAsText(file);
  };

  const handlePngExport = async () => {
    if (fileNameError) {
      setNotice({ kind: 'error', message: fileNameError });
      return;
    }
    setExporting(true);
    setNotice({ kind: 'info', message: 'Rendering full-resolution PNG…' });
    try {
      await exportPng(config, fileName);
      setNotice({ kind: 'success', message: 'PNG export completed.' });
    } catch (error) {
      setNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'PNG export failed.',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleCanvasExport = async (selection: CanvasExportSelection) => {
    if (fileNameError) {
      setNotice({ kind: 'error', message: fileNameError });
      return;
    }
    setExporting(true);
    setExportProgress(
      selection === 'composite' ? 'Rendering composite Canvas…' : 'Preparing outputs…',
    );
    try {
      if (selection === 'composite') {
        await exportCanvasPng(canvas, canvasFileName);
      } else {
        await exportCanvasArchive(canvas, canvasFileName, selection, (progress) =>
          setExportProgress(progress.message),
        );
      }
      setCanvasExportDialogOpen(false);
      setNotice({
        kind: 'success',
        message:
          selection === 'composite'
            ? 'Composite PNG exported.'
            : selection === 'resolume'
              ? 'Resolume package exported.'
              : 'Canvas ZIP exported.',
      });
    } catch (error) {
      setNotice({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Canvas export failed.',
      });
    } finally {
      setExportProgress(null);
      setExporting(false);
    }
  };

  const addItemToCanvas = (itemConfig: GeneratorConfig, editAfterAdding = false) => {
    if (itemConfig.mode !== canvas.canvasType) {
      setNotice({
        kind: 'error',
        message: `Only ${canvas.canvasType} projects can be added to this Canvas.`,
      });
      return;
    }
    const next = createCanvasItem(itemConfig, canvas.items);
    const bounds = getCanvasItemBounds(next);
    const offset = canvas.items.length * 24;
    next.x = Math.round((canvas.resolution.width - bounds.width) / 2 + offset);
    next.y = Math.round((canvas.resolution.height - bounds.height) / 2 + offset);
    setCanvas((current) => ({ ...current, items: [...current.items, next] }));
    setSelectedCanvasScreenIds([next.id]);
    if (editAfterAdding) setEditingCanvasScreenId(next.id);
    setNotice({
      kind: 'success',
      message:
        next.kind === 'led'
          ? 'LED screen added to Canvas without power cables.'
          : 'Projection added to Canvas.',
    });
  };

  const handleDuplicateCanvasScreen = (screenId: string) => {
    const source = canvas.items.find((item) => item.id === screenId);
    if (!source) return;
    if (canvas.items.length >= 200) {
      setNotice({ kind: 'error', message: 'Canvas cannot contain more than 200 items.' });
      return;
    }
    const duplicate = copyCanvasItem(source, canvas.items);
    setCanvas((current) => ({ ...current, items: [...current.items, duplicate] }));
    setSelectedCanvasScreenIds([duplicate.id]);
    setNotice({ kind: 'success', message: `Duplicated “${source.name}”.` });
  };

  const handleCopyCanvasScreen = (screenId: string) => {
    const source = canvas.items.find((item) => item.id === screenId);
    if (!source) return;
    setCanvasClipboard(structuredClone(source));
    canvasPasteOffsetRef.current = 32;
    setNotice({ kind: 'info', message: `Copied “${source.name}”.` });
  };

  const handlePasteCanvasScreen = () => {
    if (!canvasClipboard) return;
    if (canvasClipboard.kind !== canvas.canvasType) {
      setNotice({
        kind: 'error',
        message: `The copied ${canvasClipboard.kind} item cannot be pasted into a ${canvas.canvasType} Canvas.`,
      });
      return;
    }
    if (canvas.items.length >= 200) {
      setNotice({ kind: 'error', message: 'Canvas cannot contain more than 200 items.' });
      return;
    }
    const offset = canvasPasteOffsetRef.current;
    const pasted = copyCanvasItem(canvasClipboard, canvas.items, {
      x: canvasClipboard.x + offset,
      y: canvasClipboard.y + offset,
    });
    canvasPasteOffsetRef.current += 32;
    setCanvas((current) => ({ ...current, items: [...current.items, pasted] }));
    setSelectedCanvasScreenIds([pasted.id]);
    setNotice({ kind: 'success', message: `Pasted “${pasted.name}”.` });
  };

  const handleDeleteCanvasScreen = (screenId: string) => {
    const item = canvas.items.find((candidate) => candidate.id === screenId);
    if (!item || !window.confirm(`Remove “${item.name}” from this Canvas?`)) return;
    setCanvas((current) => ({
      ...current,
      items: current.items.filter((candidate) => candidate.id !== screenId),
    }));
    setSelectedCanvasScreenIds((current) => current.filter((id) => id !== screenId));
    if (editingCanvasScreenId === screenId) setEditingCanvasScreenId(null);
  };

  const handleCanvasTypeChange = (canvasType: CanvasType) => {
    if (canvasType === canvas.canvasType) return;
    if (
      canvas.items.length > 0 &&
      !window.confirm('Changing the Canvas type removes all current items. Continue?')
    ) {
      return;
    }
    const next = cloneDefaultCanvas(canvasType);
    next.resolution = { ...canvas.resolution };
    next.background = canvas.background;
    next.annotations = { ...canvas.annotations };
    setCanvas(next);
    setCurrentCanvasProjectId(null);
    setSelectedCanvasScreenIds([]);
    setEditingCanvasScreenId(null);
    setNotice({ kind: 'info', message: `New ${canvasType} Canvas created.` });
  };

  const createConfigForCanvas = () => {
    const next = cloneDefaultConfig();
    next.mode = canvas.canvasType;
    next.title = canvas.canvasType === 'led' ? 'Main Screen' : 'Main Projection';
    return next;
  };

  const handleSelectCanvasScreen = (screenId: string | null, additive = false) => {
    if (!screenId) {
      setSelectedCanvasScreenIds([]);
      return;
    }
    if (canvas.canvasType !== 'projection' || !additive) {
      setSelectedCanvasScreenIds([screenId]);
      return;
    }
    setSelectedCanvasScreenIds((current) =>
      current.includes(screenId) ? current.filter((id) => id !== screenId) : [...current, screenId],
    );
  };

  const handleDistributeCanvasSelection = (axis: 'horizontal' | 'vertical') => {
    const result = distributeProjectionItems(canvas, selectedCanvasScreenIds, axis);
    if (result.overlaps.length === 0) return;
    setCanvas(result.canvas);
    const minimum = Math.min(...result.overlaps);
    const maximum = Math.max(...result.overlaps);
    const range = minimum === maximum ? `${minimum}` : `${minimum}–${maximum}`;
    setNotice({
      kind: 'success',
      message:
        minimum >= 0
          ? `Projections distributed ${axis}. Blend widths are ${range} px.`
          : `Projections distributed ${axis} with integer-pixel spacing.`,
    });
  };

  const switchWorkspace = (mode: WorkspaceMode) => {
    setWorkspaceMode(mode);
    if (mode !== 'canvas') {
      setEditingCanvasScreenId(null);
      setCanvasExportDialogOpen(false);
    }
    const hash = mode === 'single' ? '' : `#${mode}`;
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}${hash}`,
    );
  };

  const activeProjects = workspaceMode === 'canvas' ? canvasProjects : projects;
  const activeProjectId = workspaceMode === 'canvas' ? currentCanvasProjectId : currentProjectId;
  const projectTypeLabel = workspaceMode === 'canvas' ? 'Canvas' : 'screen';

  useEffect(() => {
    if (workspaceMode !== 'canvas' || editingScreen || canvasExportDialogOpen) return;

    const handleCanvasShortcut = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      const action = getCanvasClipboardAction(event);
      if (action === 'copy' && selectedCanvasScreenId) {
        event.preventDefault();
        handleCopyCanvasScreen(selectedCanvasScreenId);
      } else if (action === 'paste' && canvasClipboard) {
        event.preventDefault();
        handlePasteCanvasScreen();
      } else if (action === 'duplicate' && selectedCanvasScreenId) {
        event.preventDefault();
        handleDuplicateCanvasScreen(selectedCanvasScreenId);
      }
    };

    window.addEventListener('keydown', handleCanvasShortcut);
    return () => window.removeEventListener('keydown', handleCanvasShortcut);
  });

  useEffect(() => {
    if (workspaceMode !== 'single' && workspaceMode !== 'canvas') return;

    const handleHistoryShortcut = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target) || event.altKey || (!event.ctrlKey && !event.metaKey)) {
        return;
      }
      const key = event.key.toLowerCase();
      const wantsUndo = key === 'z' && !event.shiftKey;
      const wantsRedo = key === 'y' || (key === 'z' && event.shiftKey);
      if (!wantsUndo && !wantsRedo) return;
      event.preventDefault();
      if (workspaceMode === 'canvas') {
        if (wantsUndo) undoCanvas();
        else redoCanvas();
      } else if (wantsUndo) {
        undoConfig();
      } else {
        redoConfig();
      }
    };

    window.addEventListener('keydown', handleHistoryShortcut);
    return () => window.removeEventListener('keydown', handleHistoryShortcut);
  }, [redoCanvas, redoConfig, undoCanvas, undoConfig, workspaceMode]);

  return (
    <div
      className="app-shell"
      onFocusCapture={(event) => {
        if (!isEditableTarget(event.target)) return;
        if (workspaceMode === 'canvas') beginCanvasBatch();
        else if (workspaceMode === 'single') beginConfigBatch();
      }}
      onBlurCapture={(event) => {
        if (!isEditableTarget(event.target)) return;
        if (workspaceMode === 'canvas') endCanvasBatch();
        else if (workspaceMode === 'single') endConfigBatch();
      }}
    >
      <header className="app-header">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            SG
          </span>
          <span>
            <strong>ScreenGrid Generator</strong>
            <small>by MKL Labs</small>
          </span>
        </div>
        <div className="header-navigation">
          <div className="workspace-switch" aria-label="Workspace tool">
            <button
              type="button"
              className={workspaceMode === 'single' ? 'is-active' : ''}
              onClick={() => switchWorkspace('single')}
            >
              Generator
            </button>
            <button
              type="button"
              className={workspaceMode === 'canvas' ? 'is-active' : ''}
              onClick={() => switchWorkspace('canvas')}
            >
              Canvas
            </button>
            <button
              type="button"
              className={workspaceMode === 'ratio' ? 'is-active' : ''}
              onClick={() => switchWorkspace('ratio')}
            >
              RatioGuard
            </button>
            <button
              type="button"
              className={workspaceMode === 'tutorial' ? 'is-active' : ''}
              onClick={() => switchWorkspace('tutorial')}
            >
              Tutorial
            </button>
          </div>
          {(workspaceMode === 'single' || workspaceMode === 'canvas') && (
            <div
              className="pattern-context-switch"
              aria-label={workspaceMode === 'canvas' ? 'Canvas type' : 'Pattern type'}
            >
              <span>{workspaceMode === 'canvas' ? 'Canvas type' : 'Pattern type'}</span>
              <div>
                <button
                  type="button"
                  className={
                    (workspaceMode === 'canvas' ? canvas.canvasType : config.mode) === 'led'
                      ? 'is-active'
                      : ''
                  }
                  disabled={Boolean(editingScreen)}
                  onClick={() => {
                    if (workspaceMode === 'canvas') handleCanvasTypeChange('led');
                    else setConfig((current) => ({ ...current, mode: 'led' }));
                  }}
                >
                  LED
                </button>
                <button
                  type="button"
                  className={
                    (workspaceMode === 'canvas' ? canvas.canvasType : config.mode) === 'projection'
                      ? 'is-active'
                      : ''
                  }
                  disabled={Boolean(editingScreen)}
                  onClick={() => {
                    if (workspaceMode === 'canvas') handleCanvasTypeChange('projection');
                    else setConfig((current) => ({ ...current, mode: 'projection' }));
                  }}
                >
                  Projection
                </button>
              </div>
            </div>
          )}
        </div>
        {workspaceMode === 'tutorial' ? (
          <div className="tutorial-header-status">
            <span>INTERACTIVE GUIDE</span>
            <strong>8 WORKFLOWS</strong>
          </div>
        ) : workspaceMode === 'ratio' ? (
          <div className="tutorial-header-status">
            <span>AV RASTER PREFLIGHT</span>
            <strong>LOCAL ONLY</strong>
          </div>
        ) : (
          <div className="project-actions">
            {editingScreen && (
              <button
                type="button"
                className="button button--primary back-to-canvas"
                onClick={() => setEditingCanvasScreenId(null)}
              >
                ← Back to Canvas
              </button>
            )}
            <label className="project-select">
              <span className="sr-only">Saved {projectTypeLabel} project</span>
              <select
                value={activeProjectId || ''}
                onChange={(event) => handleProjectSelect(event.target.value)}
              >
                <option value="">Unsaved {projectTypeLabel}</option>
                {activeProjects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="button button--ghost" onClick={handleNewProject}>
              New
            </button>
            <button type="button" className="button button--ghost" onClick={handleSaveProject}>
              Save
            </button>
            <details className="more-menu">
              <summary className="button button--ghost" aria-label="More project actions">
                •••
              </summary>
              <div className="menu-popover">
                <button type="button" onClick={handleDuplicateProject}>
                  Duplicate project
                </button>
                <button
                  type="button"
                  onClick={() =>
                    workspaceMode === 'canvas' ? downloadCanvasJson(canvas) : downloadJson(config)
                  }
                >
                  Export configuration JSON
                </button>
                <button type="button" onClick={() => jsonInputRef.current?.click()}>
                  Import configuration JSON
                </button>
                <button
                  type="button"
                  className="danger-text"
                  disabled={!activeProjectId}
                  onClick={handleDeleteProject}
                >
                  Delete saved project
                </button>
              </div>
            </details>
            <input
              ref={jsonInputRef}
              className="sr-only"
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) handleJsonImport(file);
                event.target.value = '';
              }}
            />
          </div>
        )}
      </header>

      {workspaceMode === 'ratio' ? (
        <RatioGuard />
      ) : workspaceMode === 'tutorial' ? (
        <Tutorial
          onOpenSingle={(mode) => {
            setConfig((current) => ({ ...current, mode }));
            switchWorkspace('single');
          }}
          onOpenCanvas={(type) => {
            if (type) handleCanvasTypeChange(type);
            switchWorkspace('canvas');
          }}
          onOpenRatio={() => switchWorkspace('ratio')}
        />
      ) : workspaceMode === 'canvas' && !editingScreen ? (
        <main className="workspace">
          <aside className="settings-panel">
            <CanvasControls
              canvas={canvas}
              selectedIds={selectedCanvasScreenIds}
              currentConfig={config}
              savedProjects={projects}
              updateCanvas={setCanvas}
              onSelect={handleSelectCanvasScreen}
              onDistributeSelection={handleDistributeCanvasSelection}
              onAddCurrent={() => addItemToCanvas(config)}
              onAddSaved={(projectId) => {
                const project = projects.find((item) => item.id === projectId);
                if (project) addItemToCanvas(project.config);
              }}
              onNewScreen={() => addItemToCanvas(createConfigForCanvas(), true)}
              onEditScreen={setEditingCanvasScreenId}
              onCopyScreen={handleCopyCanvasScreen}
              onPasteScreen={handlePasteCanvasScreen}
              onDuplicateScreen={handleDuplicateCanvasScreen}
              canPaste={canvasClipboard?.kind === canvas.canvasType && canvas.items.length < 200}
              onDeleteScreen={handleDeleteCanvasScreen}
            />
          </aside>
          <div className="preview-panel">
            <CanvasPreview
              canvas={canvas}
              selectedIds={selectedCanvasScreenIds}
              updateCanvas={setCanvas}
              onSelect={handleSelectCanvasScreen}
              onItemMoveStart={beginCanvasBatch}
              onItemMoveEnd={endCanvasBatch}
              canUndo={canUndoCanvas}
              canRedo={canRedoCanvas}
              onUndo={undoCanvas}
              onRedo={redoCanvas}
            />
            <ExportBar
              fileName={canvasFileName}
              fileNameError={fileNameError}
              exporting={exporting}
              onFileNameChange={setCanvasFileName}
              onExport={() => setCanvasExportDialogOpen(true)}
              buttonLabel="Export…"
            />
          </div>
        </main>
      ) : (
        <main className="workspace">
          <aside className="settings-panel">
            <Controls
              config={activeGeneratorConfig}
              updateConfig={updateActiveGeneratorConfig}
              onLogoUpload={handleLogoUpload}
              allowPowerRoutes={!editingScreen}
              modeControl={editingScreen ? 'locked' : 'header'}
            />
          </aside>
          <div className="preview-panel">
            {editingScreen && (
              <div className="canvas-edit-banner">
                <span>
                  Editing <strong>{editingScreen.name}</strong> inside {canvas.title}
                </span>
                <span>
                  {editingScreen.kind === 'led'
                    ? 'Power cables are unavailable in Canvas items.'
                    : 'Projection mode is fixed while editing this Canvas item.'}
                </span>
              </div>
            )}
            <Preview
              config={activeGeneratorConfig}
              updateConfig={updateActiveGeneratorConfig}
              allowPowerRoutes={!editingScreen}
              canUndo={editingScreen ? canUndoCanvas : canUndoConfig}
              canRedo={editingScreen ? canRedoCanvas : canRedoConfig}
              onUndo={editingScreen ? undoCanvas : undoConfig}
              onRedo={editingScreen ? redoCanvas : redoConfig}
            />
            {editingScreen ? (
              <div className="canvas-apply-bar">
                <span>Changes are already applied to this independent Canvas item.</span>
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => setEditingCanvasScreenId(null)}
                >
                  Apply & back to Canvas
                </button>
              </div>
            ) : (
              <ExportBar
                fileName={fileName}
                fileNameError={fileNameError}
                exporting={exporting}
                onFileNameChange={setFileName}
                onExport={handlePngExport}
              />
            )}
          </div>
        </main>
      )}

      <footer className="app-footer">
        <div className="footer-info">
          <span className="footer-copyright">
            ScreenGrid Generator © 2026{' '}
            <a href="https://mkl-labs.io" target="_blank" rel="noreferrer">
              MKL Labs
            </a>
            .
          </span>
          <span>
            Generator privacy: projects, settings, and logos stay in your browser and are not
            uploaded. No accounts, analytics, or tracking. Hosting may process your IP address and
            access logs.
          </span>
        </div>
        <nav className="footer-links" aria-label="Legal and developer links">
          <a href="https://mkl-labs.io/imprint">Imprint</a>
          <a href="https://mkl-labs.io/privacy">Privacy</a>
          <a href="/licenses.txt">Licenses</a>
        </nav>
      </footer>

      {canvasExportDialogOpen && (
        <CanvasExportDialog
          canvas={canvas}
          fileName={canvasFileName}
          exporting={exporting}
          progress={exportProgress}
          onClose={() => setCanvasExportDialogOpen(false)}
          onExport={(selection) => void handleCanvasExport(selection)}
        />
      )}

      {notice && (
        <div className={`notice notice--${notice.kind}`} role="status">
          {notice.message}
        </div>
      )}
    </div>
  );
}

function getInitialWorkspace(): WorkspaceMode {
  if (window.location.hash === '#tutorial') return 'tutorial';
  if (window.location.hash === '#ratio') return 'ratio';
  if (window.location.hash === '#canvas') return 'canvas';
  return 'single';
}

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

interface ExportBarProps {
  fileName: string;
  fileNameError: string | null;
  exporting: boolean;
  onFileNameChange: (value: string) => void;
  onExport: () => void;
  buttonLabel?: string;
}

function ExportBar({
  fileName,
  fileNameError,
  exporting,
  onFileNameChange,
  onExport,
  buttonLabel = 'Export PNG',
}: ExportBarProps) {
  return (
    <div className="export-bar">
      <label className="export-name">
        <span>Export file</span>
        <input
          value={fileName}
          maxLength={180}
          spellCheck={false}
          aria-invalid={Boolean(fileNameError)}
          aria-describedby={fileNameError ? 'export-file-error' : undefined}
          onChange={(event) => onFileNameChange(event.target.value)}
        />
        {fileNameError && (
          <small className="field-error" id="export-file-error">
            {fileNameError}
          </small>
        )}
      </label>
      <button
        type="button"
        className="button button--primary export-button"
        disabled={exporting || Boolean(fileNameError)}
        onClick={onExport}
      >
        {exporting ? 'Rendering…' : buttonLabel}
      </button>
    </div>
  );
}
